import type { QuotationDocumentData, StatusBadge } from '@/pages/quotation-design/types';
import QuotationHeader from './QuotationHeader';
import PartyInfo from './PartyInfo';
import CommercialSummary from './CommercialSummary';
import ProductQuoteSection from './ProductQuoteSection';
import QuoteTotals from './QuoteTotals';
import CommercialTerms from './CommercialTerms';
import DocumentNotes from './DocumentNotes';
import Authorization from './Authorization';

const badgeToneMap: Record<StatusBadge['tone'], string> = {
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  danger: 'bg-rose-50 text-rose-700 border-rose-200',
  neutral: 'bg-ink-50 text-ink-500 border-ink-200',
};

export default function QuotationDocument({ data }: { data: QuotationDocumentData }) {
  return (
    <div className="mx-auto w-full max-w-[840px]">
      {/* Status badge — outside the document body */}
      {data.statusBadge && (
        <div className="flex justify-end mb-4">
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${badgeToneMap[data.statusBadge.tone]}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current" />
            {data.statusBadge.label}
          </span>
        </div>
      )}

      {/* A4 document surface */}
      <div className="bg-white border border-ink-200/70 rounded-lg shadow-sm overflow-hidden">
        <QuotationHeader data={data} />
        <div className="px-6 md:px-9 mt-7">
          <div className="border-t-2 border-ink-700" />
        </div>
        <PartyInfo seller={data.seller} buyer={data.buyer} />
        <CommercialSummary fields={data.summary} />
        <ProductQuoteSection products={data.products} />
        <QuoteTotals rows={data.priceRows} />
        {data.priceNote && (
          <div className="px-6 md:px-9 mt-2 flex justify-end">
            <p className="w-full sm:w-[360px] md:w-[400px] text-[11px] text-ink-400 leading-relaxed">
              {data.priceNote}
            </p>
          </div>
        )}
        {data.commercialTerms && <CommercialTerms terms={data.commercialTerms} />}
        <DocumentNotes
          notes={data.notes}
          specialRequirements={data.specialRequirements}
        />
        {data.authorization && <Authorization data={data} />}

        {/* Bottom spacing */}
        <div className="h-9" />
      </div>
    </div>
  );
}